import { Center, Paper, Stack, Text, ThemeIcon, Title, Button } from '@mantine/core';
import type { ReactNode } from 'react';

export interface EmptyStateProps {
  icon?: React.ComponentType<any>;
  title: string;
  description?: string;
  children?: ReactNode;
  variant?: 'default' | 'subtle' | 'minimal';
  /** Action button */
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'filled' | 'light' | 'outline';
  };
  /** Full height container */
  fullHeight?: boolean;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  children,
  variant = 'default',
  action,
  fullHeight = false,
}: EmptyStateProps) {
  const content = (
    <Stack align="center" gap="md" p="xl" role="status" aria-live="polite">
      {Icon && (
        <ThemeIcon size={64} radius="xl" color="gray" variant="light" aria-hidden="true">
          <Icon size={32} />
        </ThemeIcon>
      )}
      <Stack align="center" gap={4}>
        <Title order={3} c="dimmed" fw={500} id="empty-state-title">
          {title}
        </Title>
        {description && (
          <Text c="dimmed" ta="center" size="sm" maw={400} id="empty-state-description">
            {description}
          </Text>
        )}
      </Stack>
      {action && (
        <Button
          variant={action.variant || 'light'}
          onClick={action.onClick}
          aria-label={action.label}
        >
          {action.label}
        </Button>
      )}
      {children}
    </Stack>
  );

  if (variant === 'subtle' || variant === 'minimal') {
    return (
      <Center py={fullHeight ? 'xl' : 'xl'} h={fullHeight ? '100%' : undefined}>
        {content}
      </Center>
    );
  }

  return (
    <Paper
      withBorder
      radius="md"
      p={0}
      style={{ borderStyle: 'dashed' }}
      aria-labelledby="empty-state-title"
      aria-describedby={description ? 'empty-state-description' : undefined}
    >
      <Center py="xl">
        {content}
      </Center>
    </Paper>
  );
}

